import * as React from 'react';

import { useLocation } from 'react-router-dom';

import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import { getWidth } from '@components/styles';
import Typography from '@components/basics/typography';
import SidebarItem from '@components/basics/sidebar-item';
import MotionOpacity from '@components/motion/motion-opacity';
import SidebarItemContent from '@components/basics/sidebar-item/sidebar-item-content';

interface SidebarSubItem {
  path: string;
  label: React.ReactNode;
  disabled?: boolean;
  pathMapper?: (path: string) => boolean;
}

export interface SidebarSection {
  path: string;
  label: React.ReactNode;
  noIcon?: boolean;
  done?: boolean;
  error?: boolean;
  disabled?: boolean;
  soon?: boolean;
  subItems?: SidebarSubItem[];
  pathMapper?: (path: string) => boolean;
  progressMax?: number;
  progressFilled?: number | null;
}

interface SidebarCtrlProps {
  className?: string;
  children?: any;
  title?: React.ReactNode;
  customMarginTitle?: number;
  outsideLink?: React.ReactElement;
  sections?: SidebarSection[];
}

export const SidebarCtrl: React.FC<SidebarCtrlProps> = (props) => {
  const location = useLocation();

  const isActive = (
    section: string,
    pathMapper?: SidebarSection['pathMapper']
  ) => {
    if (pathMapper) {
      return pathMapper(location.pathname);
    }
    const path = section.split('/').slice(-1)[0];
    return location.pathname.endsWith(path);
  };

  return (
    <div className={props.className}>
      {props.title && (
        <Typography className="title" type="title" customFont="medium">
          {props.title}
        </Typography>
      )}
      {props.sections
        ? props.sections.map((section) => (
            <SidebarItem
              key={`sidebar-section-${section.path}`}
              route={section.path}
              active={isActive(section.path, section.pathMapper)}
              done={section.done}
              disabled={section.disabled}
              soon={section.soon}
              hasSubItem={!!section.subItems}
              error={section.error}
            >
              <SidebarItemContent
                noIcon={section.noIcon}
                active={isActive(section.path, section.pathMapper)}
                disabled={section.disabled}
                text={section.label}
                done={section.done}
                error={section.error}
              />
              <AnimatePresence exitBeforeEnter>
                {section.subItems &&
                  isActive(section.path, section.pathMapper) && (
                    <MotionOpacity>
                      {section.subItems.map((subItem) => (
                        <SidebarItem
                          isSubItem
                          key={`sidebar-section-subitem-${subItem.path}`}
                          route={subItem.path}
                          active={isActive(subItem.path, subItem.pathMapper)}
                          disabled={subItem.disabled}
                          error={section.error}
                        >
                          <SidebarItemContent
                            noIcon={section.noIcon}
                            active={isActive(subItem.path, subItem.pathMapper)}
                            disabled={subItem.disabled}
                            text={subItem.label}
                            error={section.error}
                          />
                        </SidebarItem>
                      ))}
                    </MotionOpacity>
                  )}
              </AnimatePresence>
            </SidebarItem>
          ))
        : props.children}

      {props.outsideLink ? props.outsideLink : null}
    </div>
  );
};

export default styled(SidebarCtrl)`
  .sidebar {
    width: 100%;

    @media ${({ theme }) => theme.device.tablet} {
      width: auto;
      max-width: ${getWidth(6)};
    }
  }
  .title {
    margin-bottom: ${(props) =>
      props.customMarginTitle ? `${props.customMarginTitle}px` : '20px'};
  }
`;
